import React, { useEffect, useState } from 'react';
import { settingsApiFull, exportApi } from '../api';
import { Btn, Input, Alert, Spinner } from '../components/ui';

export default function Settings() {
  const [form,    setForm]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [success, setSuccess] = useState('');
  const [error,   setError]   = useState('');
  const [newTag,  setNewTag]  = useState('');
  const [newHol,  setNewHol]  = useState('');

  useEffect(() => {
    settingsApiFull.get()
      .then(r => setForm(r.data.data))
      .catch(() => setError('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setError(''); setSuccess(''); setSaving(true);
    try {
      const res = await settingsApiFull.update(form);
      if (res.data.success) { setForm(res.data.data); setSuccess('Settings saved successfully'); }
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const addTag = () => {
    const t = newTag.trim();
    if (!t || form.masterTags?.includes(t)) return;
    set('masterTags', [...(form.masterTags || []), t]);
    setNewTag('');
  };

  const removeTag = (t) => set('masterTags', form.masterTags.filter(x => x !== t));

  const addHoliday = () => {
    if (!newHol || form.holidays?.includes(newHol)) return;
    set('holidays', [...(form.holidays || []), newHol].sort());
    setNewHol('');
  };

  const removeHoliday = (d) => set('holidays', form.holidays.filter(h => h !== d));

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
      <Spinner size={28} />
    </div>
  );

  return (
    <div style={{ padding: '24px', maxWidth: '680px' }}>
      <h1 style={S.pageTitle}>System Settings</h1>
      <p style={S.pageSubtitle}>Configure company-wide defaults. Changes take effect immediately.</p>

      {success && <Alert type="success" message={success} />}
      {error   && <Alert type="error"   message={error}   />}

      {/* Work Calendar */}
      <Section title="Work Calendar">
        <div>
          <label style={S.label}>Work week</label>
          <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
            {[5, 6].map(v => (
              <Chip key={v} active={form.workDaysPerWeek === v} onClick={() => set('workDaysPerWeek', v)}>
                {v} days — {v === 5 ? 'Mon–Fri' : 'Mon–Sat'}
              </Chip>
            ))}
          </div>
        </div>
        <div style={{ marginTop: '20px' }}>
          <label style={S.label}>Work start day</label>
          <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
            {['MONDAY', 'SUNDAY'].map(v => (
              <Chip key={v} active={form.workStartDay === v} onClick={() => set('workStartDay', v)}>
                {v === 'MONDAY' ? 'Monday' : 'Sunday'}
              </Chip>
            ))}
          </div>
        </div>
        <div style={{ marginTop: '20px' }}>
          <label style={S.label}>Company Holidays</label>
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px', marginBottom: '10px' }}>
            <input type="date" value={newHol} onChange={e => setNewHol(e.target.value)}
              style={{ flex: 1, padding: '10px 12px', borderRadius: 'var(--radius-md)', border: '1.5px solid var(--color-border)', fontSize: '14px' }} />
            <Btn variant="secondary" size="sm" onClick={addHoliday}>Add</Btn>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {(form.holidays || []).map(d => (
              <TagPill key={d} onRemove={() => removeHoliday(d)}>{d}</TagPill>
            ))}
          </div>
        </div>
      </Section>

      {/* Authentication */}
      <Section title="Authentication">
        <Input
          label="Default password for new employees"
          value={form.defaultPassword}
          onChange={e => set('defaultPassword', e.target.value)}
          placeholder="Tracern@123"
        />
        <div style={{ marginTop: '16px' }}>
          <label style={S.label}>Session timeout</label>
          <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
            {[30, 60, 120, 240].map(v => (
              <Chip key={v} active={form.sessionTimeoutMinutes === v} onClick={() => set('sessionTimeoutMinutes', v)}>
                {v}m
              </Chip>
            ))}
          </div>
        </div>
      </Section>

      {/* Notifications */}
      <Section title="Notifications">
        <Input
          label="Daily digest time (24h)"
          type="time"
          value={form.digestTime}
          onChange={e => set('digestTime', e.target.value)}
        />
        <div style={{ marginTop: '16px' }}>
          <label style={S.label}>On-hold escalation threshold (working hours)</label>
          <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
            {[4, 8, 24, 48].map(v => (
              <Chip key={v} active={form.onHoldThresholdHours === v} onClick={() => set('onHoldThresholdHours', v)}>
                {v}h
              </Chip>
            ))}
          </div>
        </div>
      </Section>

      {/* Tags */}
      <Section title="Master Tag List">
        <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginBottom: '12px' }}>
          All task tags are picked from this list. Employees cannot create free-form tags.
        </p>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
          <input
            placeholder="e.g. client-facing, legal, urgent"
            value={newTag}
            onChange={e => setNewTag(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addTag()}
            style={{ flex: 1, padding: '10px 12px', borderRadius: 'var(--radius-md)', border: '1.5px solid var(--color-border)', fontSize: '14px' }}
          />
          <Btn variant="secondary" size="sm" onClick={addTag}>Add</Btn>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {(form.masterTags || []).map(t => (
            <TagPill key={t} onRemove={() => removeTag(t)}>{t}</TagPill>
          ))}
        </div>
      </Section>


      {/* Security Policy */}
      <Section title="Security Policy">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
          <div style={{ flex: 1 }}>
            <p style={{ fontWeight: 600, fontSize: '14px', color: 'var(--color-text)', marginBottom: '4px' }}>
              Require MFA for all employees
            </p>
            <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', lineHeight: 1.65 }}>
              When enabled, every employee must configure two-factor authentication (Google Authenticator) before accessing the app. Employees who have not yet set up MFA will be prompted to do so on next login.
            </p>
          </div>
          <button
            onClick={() => setForm(f => ({ ...f, mfaRequired: !f.mfaRequired }))}
            style={{
              flexShrink: 0, width: '48px', height: '26px', borderRadius: '13px',
              border: 'none', cursor: 'pointer', transition: 'background 0.2s',
              background: form?.mfaRequired ? 'var(--color-primary)' : '#D1D5DB',
              position: 'relative',
            }}
          >
            <span style={{
              position: 'absolute', top: '3px',
              left: form?.mfaRequired ? '25px' : '3px',
              width: '20px', height: '20px', borderRadius: '50%',
              background: '#fff', transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }} />
          </button>
        </div>
        <p style={{
          marginTop: '10px', fontSize: '12px', fontWeight: 600,
          color: form?.mfaRequired ? 'var(--color-primary)' : 'var(--color-text-muted)',
        }}>
          {form?.mfaRequired ? '✓ MFA is required for all employees' : 'MFA is optional — employees can choose to enable it'}
        </p>
      </Section>
      <div style={{ marginTop: '24px' }}>
        <Btn onClick={save} loading={saving} size="lg">Save Settings</Btn>
      </div>

      <ExportSection />
    </div>
  );
}

function ExportSection() {
  const [exporting,  setExporting]  = useState(false);
  const [exportError,setExportError]= useState('');
  const [exported,   setExported]   = useState(false);

  const handleExport = async () => {
    setExporting(true); setExportError(''); setExported(false);
    try {
      const res = await exportApi.downloadMyData();
      const url = URL.createObjectURL(res.data);
      const a   = document.createElement('a');
      a.href    = url;
      a.download = 'tracern-company-export.zip';
      a.click();
      URL.revokeObjectURL(url);
      setExported(true);
    } catch (e) {
      setExportError('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{
      background: 'var(--color-surface)', borderRadius: 'var(--radius-lg)',
      border: '1px solid var(--color-border)', padding: '20px',
      marginTop: '20px',
    }}>
      <p style={{ fontSize: '15px', fontWeight: 600, color: 'var(--color-primary)', marginBottom: '8px' }}>
        Data Export
      </p>
      <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', lineHeight: 1.6, marginBottom: '16px' }}>
        Download a complete export of all your company data — employees, departments, tasks,
        notes, audit logs, and notifications — as Excel files in a ZIP archive.
        This export contains everything Tracern holds about your organisation.
      </p>
      {exportError && <Alert type="error" message={exportError} />}
      {exported && (
        <div style={{ padding: '10px 14px', background: '#F0FFF4', border: '1px solid #9AE6B4',
          borderRadius: 'var(--radius-sm)', fontSize: '13px', color: 'var(--color-success)', marginBottom: '12px' }}>
          Export downloaded successfully.
        </div>
      )}
      <Btn variant="secondary" onClick={handleExport} loading={exporting}>
        ↓ Export All Company Data
      </Btn>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{
      background: 'var(--color-surface)', borderRadius: 'var(--radius-lg)',
      border: '1px solid var(--color-border)', padding: '20px 20px',
      marginTop: '20px',
    }}>
      <p style={{ fontSize: '15px', fontWeight: 600, color: 'var(--color-primary)', marginBottom: '16px' }}>{title}</p>
      {children}
    </div>
  );
}

function Chip({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '8px 16px', borderRadius: 'var(--radius-md)', cursor: 'pointer',
      fontSize: '13px', fontWeight: 500, transition: 'all 0.15s',
      background: active ? 'var(--color-primary)' : 'var(--color-surface)',
      color:      active ? '#fff' : 'var(--color-text-secondary)',
      border:     `1.5px solid ${active ? 'var(--color-primary)' : 'var(--color-border)'}`,
    }}>{children}</button>
  );
}

function TagPill({ children, onRemove }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      padding: '4px 10px', borderRadius: '999px', fontSize: '12px',
      background: 'var(--color-primary-lighter)', color: 'var(--color-primary)',
    }}>
      {children}
      <button onClick={onRemove} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', fontSize: '15px', lineHeight: 1, padding: 0 }}>×</button>
    </span>
  );
}

const S = {
  pageTitle:   { fontSize: '22px', fontWeight: 600, color: 'var(--color-primary)', marginBottom: '4px' },
  pageSubtitle:{ fontSize: '14px', color: 'var(--color-text-muted)', marginBottom: '20px' },
  label:       { fontSize: '13px', fontWeight: 500, color: 'var(--color-text-secondary)' },
};

export function DevTools() {
  const [status, setStatus] = useState('');
  const { schedulerApi } = require('../api');

  const trigger = async (fn, label) => {
    setStatus('Running ' + label + '...');
    try {
      await fn();
      setStatus(label + ' completed successfully');
    } catch (e) {
      setStatus('Error: ' + (e.response?.data?.message || e.message));
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '680px' }}>
      <h1 style={{ fontSize: '22px', fontWeight: 600, color: 'var(--color-primary)', marginBottom: '4px' }}>Developer Tools</h1>
      <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginBottom: '20px' }}>
        Manually trigger scheduled jobs. Use this during development to test notifications without waiting for cron.
      </p>
      {status && (
        <div style={{ padding: '10px 14px', background: 'var(--color-bg)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', marginBottom: '16px', fontSize: '13px', color: 'var(--color-text-secondary)' }}>
          {status}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {[
          { label: 'Run Overdue Detection', fn: schedulerApi.runOverdue, desc: 'Marks past-deadline tasks as OVERDUE and fires notifications' },
          { label: 'Run Daily Digest',      fn: schedulerApi.runDigest,  desc: 'Sends morning digest to all active users' },
          { label: 'Run On-Hold Check',     fn: schedulerApi.runOnHold,  desc: 'Fires escalation alerts for long-running on-hold tasks' },
        ].map(({ label, fn, desc }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: 'var(--color-surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
            <div>
              <p style={{ fontWeight: 500, fontSize: '14px' }}>{label}</p>
              <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '2px' }}>{desc}</p>
            </div>
            <button onClick={() => trigger(fn, label)} style={{
              padding: '8px 14px', borderRadius: 'var(--radius-sm)',
              background: 'var(--color-primary)', color: '#fff',
              border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 500, flexShrink: 0,
            }}>Run</button>
          </div>
        ))}
      </div>
    </div>
  );
}
